// src/services/brand-access-service.ts
import { brandsApi, type MyBrands, type BrandSummary, type BrandRunSummary, type BrandRunDetail } from "@/api/brands";

export type { BrandSummary, BrandRunSummary, BrandRunDetail };

/**
 * Brand-scoping service. Mirrors the storage + helper style of
 * auth-service.ts so it slots into App.tsx without a new context.
 *
 * A "brand-scoped user" is any non-admin user. They:
 *   - cannot open the brand-search / onboarding pages
 *   - only see brands an admin assigned to them
 *   - land on Brand Monitoring (dashboard) after login
 *   - switch brands via the bottom-of-nav switcher
 *
 * To narrow brand-scoping to a specific user_type later (e.g. only
 * "basic"), change isBrandScopedUser() — that's the single switch.
 */

const ACTIVE_BRAND_KEY = "up_active_brand";

function getIsAdmin(): boolean {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

export function isBrandScopedUser(): boolean {
  // All non-admins are brand-scoped. (Single point of control.)
  return !getIsAdmin();
}

export function getCachedActiveBrand(): string {
  return (
    localStorage.getItem(ACTIVE_BRAND_KEY) ||
    localStorage.getItem("bm_brand") ||
    ""
  );
}

function cacheActiveBrand(brand: string | null) {
  if (brand) {
    localStorage.setItem(ACTIVE_BRAND_KEY, brand);
    // keep App.tsx's existing nav source in sync
    localStorage.setItem("bm_brand", brand);
  } else {
    localStorage.removeItem(ACTIVE_BRAND_KEY);
  }
}

export interface BrandScope extends MyBrands {}

/** Fetch the caller's assigned brands + active brand. */
export async function getMyBrands(): Promise<BrandScope> {
  const res = await brandsApi.myBrands();
  cacheActiveBrand(res.active_brand);
  return res;
}

/**
 * Switch the active brand server-side. Returns the refreshed scope.
 * Also broadcasts a `brand:changed` event so any open module can
 * reload — same CustomEvent pattern as `analytics:refresh`.
 */
export async function switchActiveBrand(brand: string): Promise<BrandScope> {
  const res = await brandsApi.setActive(brand);
  cacheActiveBrand(res.active_brand);
  try {
    window.dispatchEvent(
      new CustomEvent("brand:changed", { detail: { brand: res.active_brand } }),
    );
  } catch {
    /* no-op */
  }
  return res;
}

export function clearBrandCache() {
  localStorage.removeItem(ACTIVE_BRAND_KEY);
}

/** Run history for an assigned brand (newest first). */
export async function listBrandRuns(brand: string): Promise<BrandRunSummary[]> {
  return brandsApi.listRuns(brand);
}

/** Full data + selected models for one run of an assigned brand. */
export async function getBrandRun(
  runId: number,
  brand: string,
): Promise<BrandRunDetail> {
  return brandsApi.getRun(runId, brand);
}